import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, ShieldCheck } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ceo-operacional/artifacts/2hvzdama_LogocomSlogan-fundoescuro.png";

export default function Recover() {
  const navigate = useNavigate();
  const { recover, securityQuestion } = useAuth();
  const [form, setForm] = useState({ security_answer: "", new_password: "", new_password_confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 12) return toast.error("Nova senha precisa ter no mínimo 12 caracteres");
    if (form.new_password !== form.new_password_confirm) return toast.error("As novas senhas não conferem");
    setLoading(true);
    try {
      await recover({
        security_answer: form.security_answer,
        new_password: form.new_password,
      });
      toast.success("Senha redefinida. Faça login com a nova senha.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha na recuperação");
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background px-4 py-8 sm:px-6">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-primary/10 to-transparent" />
      <div className="pointer-events-none absolute -left-24 top-16 size-72 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-lg items-center">
        <div className="w-full animate-fade-in">
          <div className="mb-6 text-center">
            <img src={LOGO_URL} alt="DEMoraes" className="mx-auto mb-5 h-16 object-contain" />
            <div className="label-tech">Segurança da conta</div>
            <h1 className="mt-2 text-2xl font-semibold tracking-[-0.04em] sm:text-3xl">Recuperar acesso</h1>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">Confirme sua pergunta de segurança e defina uma nova senha de acesso.</p>
          </div>

          <div className="rounded-2xl border border-border/80 bg-card/90 p-5 shadow-[0_1px_2px_hsl(var(--foreground)/0.04),0_24px_70px_hsl(var(--foreground)/0.07)] backdrop-blur-sm sm:p-7" data-testid="recover-form">
            <div className="mb-5 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/[0.055] p-3.5">
              <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><ShieldCheck className="size-4" /></div>
              <div>
                <div className="text-sm font-medium">Verificação de identidade</div>
                <div className="mt-0.5 text-xs leading-relaxed text-muted-foreground">A resposta correta é necessária antes de alterar a senha administrativa.</div>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="label-tech">Pergunta de segurança</Label>
                <div className="rounded-xl border border-border/70 bg-muted/30 px-3.5 py-3 text-sm leading-relaxed" data-testid="recover-security-question">
                  {securityQuestion || "Carregando pergunta de segurança..."}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="label-tech">Resposta</Label>
                <Input value={form.security_answer} onChange={(e) => setForm({ ...form, security_answer: e.target.value })} data-testid="recover-answer-input" autoFocus placeholder="Sua resposta" />
              </div>
              <div className="space-y-1.5">
                <Label className="label-tech">Nova senha de acesso</Label>
                <Input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="recover-new-password-input" placeholder="Mínimo de 12 caracteres" />
              </div>
              <div className="space-y-1.5">
                <Label className="label-tech">Confirmar nova senha</Label>
                <Input type="password" value={form.new_password_confirm} onChange={(e) => setForm({ ...form, new_password_confirm: e.target.value })} data-testid="recover-new-password-confirm-input" placeholder="Repita a nova senha" />
              </div>
              <Button type="submit" disabled={loading} className="mt-1 w-full" data-testid="recover-submit-btn">
                <KeyRound className="size-4" /> {loading ? "Redefinindo..." : "Redefinir senha"}
              </Button>
            </form>

            <div className="mt-5 text-center">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition hover:text-primary" data-testid="back-to-login-link">
                <ArrowLeft className="size-3.5" /> Voltar ao login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
